/**
 * 「评估方法」抽屉的内容
 *
 * 读者是谁：运营和管理员。他们想知道的是**"这页的数是怎么来的"**，
 * 不是引擎版本、载荷指纹、内部规则桶 —— 那些东西看不懂，也不会有人去看，
 * 写了只会把真正要看的几句淹掉。所以这里只留四件事：
 *   这段时间统计了什么、对照了哪些标准、拿什么做比较、这一页不做什么。
 *
 * ⚠ 两条仍然生效的约束：
 *  1. 本节没有可签字的医学/营养评审人 ⇒ 只讲口径，不给评价性结论；
 *  2. 引注（CITATIONS）是方法披露，不是"结论有背书" —— 载荷里刻意不带 citations 字段（D11/D12）。
 *     B 档放开后，WHO/AASM 两类外部参考值**会**出现在卡片结论里做对照（见 D14 修订），
 *     但它们不参与打分，这层区别必须在抽屉里讲清楚。
 *
 * 改动纪律：抽屉正文和 copy.js 一样属于文案层（R15：改一句话不该走组件改造），
 * 而 buildChecks 的禁用词与"技术术语"扫描只扫文案层，扫得越集中越准。
 */

/** 抽屉里的按钮标题与字段名（不得留在 JSX 里 —— buildChecks 扫 v2/*.jsx 含中文即红）。 */
export const METHOD_UI = {
  trigger: '评估方法',
  title: '评估方法',
  citationsTitle: '依据来源',
  bullet: '· ',
  empty: '-',
};

/** 抽屉正文：每条都是"这一页的数怎么来的"，写给不写代码的人看。 */
export const METHOD_SECTIONS = [
  {
    key: 'window',
    title: '统计范围',
    lines: [
      '统计最近 42 天，截止到昨天（今天的数据还不完整，不纳入）。',
      '这 42 天按 7 天一组排列，方便看出哪一段时间记得多、哪一段断开了。',
      '这是行为记录的时间范围，不等于习惯已经养成：习惯的自动化通常以十周计。',
    ],
  },
  {
    key: 'standard',
    title: '参考了哪些标准',
    lines: [
      '运动量：成年人每周至少 150 分钟中等强度活动（世界卫生组织 2020 年指南）。',
      '睡眠时长：成年人每晚 7–9 小时（美国睡眠医学会 2015 年推荐）。',
      '上面两条只出现在卡片的结论里做对照，不参与打分。',
      '饮食与心理没有公认的通用阈值，所以只统计记录次数与连续性，不做对照。',
    ],
  },
  {
    key: 'baseline',
    title: '拿什么做比较',
    lines: [
      '主要是跟这个用户自己比：同一维度内，取他这段时间的常见数值区间（P25–P75）作为参照。',
      '他自己的记录还太少时，不画参照区间，也不拿上面的标准顶替。',
    ],
  },
  {
    key: 'score',
    title: '分数怎么看',
    lines: [
      '四个维度各自给一个 0–100 的分，不合并成一个总分。',
      '记录天数太少时，分数会标注「样本不足，临时分」，说明这个分数还不够稳。',
      '分数低不等于做得少，也可能只是这段时间没怎么记录。',
    ],
  },
  {
    key: 'boundary',
    title: '这一页不做什么',
    lines: [
      '不做诊断，也不替用户做健康或安全上的判断。',
      '不评价人的性格，也不给人分级。',
      '心情打卡只统计记了几次、连不连续，不等同于任何情绪量表。',
    ],
  },
];

/**
 * 依据来源。只登记用户可能想去查的**公开标准**（写成中文简称，不写期刊卷期），
 * 内部统计方法（稳健斜率、缺失值处理之类）不在这里展开 —— 那是研发文档的事。
 */
export const CITATIONS = [
  {
    id: 'lally-2010',
    work: 'Lally 等，2010（习惯自动化的形成时间研究）',
    usedFor:
      '窗口长度的依据：习惯行为自动化通常以十周计，短窗口不足以判断"已经养成"。',
  },
  {
    id: 'who-2020',
    work: '世界卫生组织（WHO）2020 年《身体活动与久坐行为指南》',
    usedFor:
      '运动量的外部参考：成年人每周至少 150 分钟中等强度活动。只出现在卡片结论里做对照，不参与打分。',
  },
  {
    id: 'aasm-2015',
    work: '美国睡眠医学会（AASM）2015 年健康成人睡眠时长推荐',
    usedFor:
      '睡眠时长的外部参考：成年人每晚 7–9 小时。只出现在卡片结论里做对照，不参与打分。',
  },
  {
    id: 'acsm-fittvp',
    work: 'ACSM《运动测试与处方指南》（第 11 版）',
    usedFor:
      '运动维度按"频次 / 时长 / 类型 / 运动量"四个角度看，不做强度分档。',
  },
  {
    id: 'michie-2013',
    work: 'Michie 等，2013（自我监测研究）',
    usedFor: '饮食与心理只看"记没记、连着几天"，不评价记得好不好。',
  },
  {
    id: 'atwater',
    work: 'Atwater 系数（蛋白质与碳水 4 千卡每克、脂肪 9 千卡每克）',
    usedFor:
      '饮食卡片的“三大营养素供能占比”：把克数折成供能后再比较，避免按克数比时脂肪被读成“吃得少”。只用于展示，不参与打分。',
  },
  {
    id: 'not-used-for-scoring',
    work: '其他文献给出的数值区间（如急慢性负荷比、正念干预）',
    usedFor:
      '明确**不采用**于判定与打分：这些数值要进入判定需要评审人背书，本期没有。',
  },
];

/** 抽屉里对"两套口径"的固定一句（R5 要求方法区讲清与商家端不可互推）。 */
export const METHOD_FOOTNOTE =
  '本页与商家端「健康习惯」相关展示分属两套计算口径：时间范围、判据与命名都不同，同一用户两侧数字不同是预期，不是数据错。';
